import { useState } from 'react';
import { useDeal } from '../../../hooks/useDeal';
import { useDealsStore } from '../../../store/deals';
import { renderInlineMd, nowIso } from '../../../lib/utils';
import { regeneratePicture } from '../../../services/claude';
import type { OwnershipPicture } from '../../../types';

interface Props { dealId: string; }

export function OverviewTab({ dealId }: Props) {
  const { deal, data } = useDeal(dealId);
  const savePicture = useDealsStore(s => s.savePicture);
  const [busy, setBusy] = useState(false);

  if (!deal) return null;

  const onRegenerate = async () => {
    setBusy(true);
    try {
      const out = await regeneratePicture({
        deal,
        files: data.files,
        findings: data.findings,
        chain: data.chain,
        picture: data.picture,
        tracts: data.tracts,
        packageDraft: data.packageDraft,
        recentLeadEvents: data.leadEvents.slice(0, 8),
        conversation: [],
      });
      const picture: OwnershipPicture = {
        dealId,
        narrative: out.narrative,
        confidence: out.confidence,
        generatedAt: nowIso(),
        stats: data.picture?.stats ?? {},
      };
      await savePicture(picture);
    } finally {
      setBusy(false);
    }
  };

  const p = data.picture;
  const stats = p?.stats ?? {};

  return (
    <>
      <div className="workspace-head">
        <div>
          <h2 className="workspace-title">
            Ownership <em>picture</em>
          </h2>
          <div className="workspace-subtitle">
            What we know · {data.findings.length} findings · {data.files.length} files{p && <> · confidence {p.confidence}%</>}
          </div>
        </div>
        <div className="workspace-actions">
          <div className="picture-confidence">
            <span>Confidence</span>
            <div className="conf-bar">
              <div className="conf-fill" style={{ width: `${p?.confidence ?? 0}%` }} />
            </div>
            <span>{p?.confidence ?? 0}%</span>
          </div>
          <button className="btn-primary" onClick={onRegenerate} disabled={busy}>
            {busy ? 'Regenerating…' : 'Regenerate'}
          </button>
        </div>
      </div>

      <div className="picture-grid">
        <Stat label="NMA"             value={stats.nma ?? deal.nmaEstimate?.toString() ?? '—'} unit="net mineral acres" />
        <Stat label="Royalty decimal" value={stats.royaltyDecimal ?? '—'} unit={deal.royalty} />
        <Stat label="Lease expires"   value={stats.leaseExpires ?? '—'} unit={deal.leaseExpires?.slice(0, 4)} />
        <Stat label="Production"      value={stats.productionStatus ?? deal.status} />
      </div>

      {p ? (
        <div className="narrative">
          <div dangerouslySetInnerHTML={{ __html: renderInlineMd(p.narrative) }} />
          <div className="narrative-meta">
            Authored by Claude · regenerated {new Date(p.generatedAt).toLocaleString()}
          </div>
        </div>
      ) : (
        <div className="narrative-empty">No picture yet · click "Regenerate"</div>
      )}

      {data.tracts.map(tract => (
        <div key={tract.id} className="tract-card">
          <div className="tract-head">
            <span className="tract-trs">{tract.trs}</span>
            <div className="tract-status">
              {tract.status.map(s => (
                <span key={s} className={`tract-chip ${s.toLowerCase().replace(' ', '-').includes('produc') ? 'producing' : s.toLowerCase().includes('leas') ? 'leased' : ''}`}>
                  {s}
                </span>
              ))}
            </div>
          </div>
          <div className="tract-grid">
            <Field label="Basin"           value={tract.basin} />
            <Field label="Gross / Net"     value={`${tract.grossAcres} / ${tract.netAcres}`} />
            <Field label="Royalty"         value={tract.royalty} />
            <Field label="Burdens"         value={tract.burdens} />
            <Field label="Active wells"    value={String(tract.activeWells ?? '—')} />
            <Field label="DUCs"            value={String(tract.ducs ?? '—')} />
            <Field label="Permits 90d"     value={String(tract.permits90d ?? '—')} />
            <Field label="Last 12mo BOE"   value={tract.last12moBoe ? tract.last12moBoe.toLocaleString() : '—'} />
          </div>
          <div className="tract-external">
            <span className="tract-link" title="Open in Energy Domain (deep link)">
              [ED] Open in Energy Domain →
            </span>
            <span className="tract-link" title="Open in ECMC eFile">
              [ECMC] eFile →
            </span>
            <span className="tract-link" title="Open in Weld Clerk">
              [Clerk] recording.weld.gov →
            </span>
          </div>
        </div>
      ))}
    </>
  );
}

function Stat({ label, value, unit }: { label: string; value: string; unit?: string }) {
  return (
    <div className="picture-stat">
      <div className="picture-stat-label">{label}</div>
      <div className="picture-stat-value">
        <em>{value}</em>{unit && <span className="unit">{unit}</span>}
      </div>
    </div>
  );
}
function Field({ label, value }: { label: string; value: string }) {
  return (
    <div>
      <div className="tract-field-label">{label}</div>
      <div className="tract-field-value">{value}</div>
    </div>
  );
}
